// src/pages/AudienceDisplay.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { 
  cacheGameData, 
  getCachedGameData
  // subscribeToHostStageInFirestore // Removed unused import
} from '../utils/gameSync';
import { collection, getDocs, query, where } from 'firebase/firestore';



const AudienceDisplay = () => {
  const { id } = useParams();
  const [gameData, setGameData] = useState(null);
  const [currentStage, setCurrentStage] = useState(1);
  const [revealAnswer, setRevealAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slides, setSlides] = useState([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [scoreAnnouncementIndex, setScoreAnnouncementIndex] = useState(-1);
  const [teams, setTeams] = useState([]); // -1 = not announcing, 0+ = team index
  
  // Get slides for the current stage
  const getSlidesForCurrentStage = () => {
    const stageMap = {
      1: { stage: 'pregame', category: null },           // Stage 1 = pregame (all categories)
      14: { stage: 'halftime', category: 'gameInfo' },   // Stage 14 = halftime rules
      17: { stage: 'halftime', category: 'ads' },        // Stage 17 = halftime ads
      31: { stage: 'final', category: 'ads' },           // Stage 31 = pre-final ads
      33: { stage: 'final', category: 'gameInfo' },      // Stage 33 = pre-final intro slides
      37: { stage: 'tiebreaker', category: null }        // Stage 37 = tiebreaker info slides (all categories)
    };
    
    const stageConfig = stageMap[currentStage] || { stage: 'pregame', category: null };
    let stageSlides;
    
    if (stageConfig.category) {
      // Filter by both stage and category
      stageSlides = slides.filter(slide => 
        slide.stage === stageConfig.stage && slide.category === stageConfig.category
      );
    } else {
      // Filter by stage only (for stages that show all categories)
      stageSlides = slides.filter(slide => slide.stage === stageConfig.stage);
    }
    
    return stageSlides;
  };
  
  // Use ref to track latest scoreAnnouncementIndex for immediate access
  const scoreAnnouncementIndexRef = useRef(-1);

  // Helper functions for progress and upcoming categories
  const getProgressInfo = (stage) => {
    if (stage === 1) return { quarter: 'Pre-Game', question: null, totalQuestions: null };
    if (stage === 2) return { quarter: 'Quarter 1', question: 'Categories', totalQuestions: 5 };
    if (stage >= 3 && stage <= 7) return { quarter: 'Quarter 1', question: stage - 2, totalQuestions: 5 };
    if (stage === 8) return { quarter: 'Quarter 2', question: 'Categories', totalQuestions: 5 };
    if (stage >= 9 && stage <= 13) return { quarter: 'Quarter 2', question: stage - 8, totalQuestions: 5 };
    if (stage >= 14 && stage <= 18) return { quarter: 'Halftime', question: stage === 16 ? 1 : null, totalQuestions: 1 };
    if (stage === 19) return { quarter: 'Quarter 3', question: 'Categories', totalQuestions: 5 };
    if (stage >= 20 && stage <= 24) return { quarter: 'Quarter 3', question: stage - 19, totalQuestions: 5 };
    if (stage === 25) return { quarter: 'Quarter 4', question: 'Categories', totalQuestions: 5 };
    if (stage >= 26 && stage <= 30) return { quarter: 'Quarter 4', question: stage - 25, totalQuestions: 5 };
    if (stage >= 31 && stage <= 36) return { quarter: 'Final', question: stage === 35 ? 1 : null, totalQuestions: 1 };
    if (stage === 36) return { quarter: 'Tie Detection', question: null, totalQuestions: null };
    if (stage === 38) return { quarter: 'Tie Breaker', question: 1, totalQuestions: 1 };
    if (stage === 39) return { quarter: 'Final Results', question: null, totalQuestions: null };
    return { quarter: 'Game Over', question: null, totalQuestions: null };
  };

  const getUpcomingCategories = (stage, gameData) => {
    if (!gameData) return [];
    
    const { quarter1, quarter2, quarter3, quarter4, halftime, final } = gameData;
    
    // Get current quarter and question number
    const progress = getProgressInfo(stage);
    const currentQuarter = progress.quarter;
    const currentQuestion = progress.question;
    
    // If we're on categories stage, show all categories for that quarter
    if (currentQuestion === 'Categories') {
      if (currentQuarter === 'Quarter 1') return quarter1?.map(q => q.category) || [];
      if (currentQuarter === 'Quarter 2') return quarter2?.map(q => q.category) || [];
      if (currentQuarter === 'Quarter 3') return quarter3?.map(q => q.category) || [];
      if (currentQuarter === 'Quarter 4') return quarter4?.map(q => q.category) || [];
      if (currentQuarter === 'Halftime') return halftime?.category ? [halftime.category] : [];
      if (currentQuarter === 'Final') return final?.category ? [final.category] : [];
    }
    
    // If we're on a question, show remaining categories in current quarter
    if (typeof currentQuestion === 'number') {
      let quarter;
      if (currentQuarter === 'Quarter 1') quarter = quarter1;
      else if (currentQuarter === 'Quarter 2') quarter = quarter2;
      else if (currentQuarter === 'Quarter 3') quarter = quarter3;
      else if (currentQuarter === 'Quarter 4') quarter = quarter4;
      else if (currentQuarter === 'Halftime') return [];
      else if (currentQuarter === 'Final') return [];
      else return [];
      
      if (!quarter) return [];
      
      // Return remaining categories (current question and beyond)
      return quarter.slice(currentQuestion - 1).map(q => q.category);
    }
    
    return [];
  };

  // 🎯 FAN-OUT: Load game once and cache locally
  useEffect(() => {
    const loadGameLocally = async () => {
      if (!id) {
        setError('No game ID provided');
        setLoading(false);
        return;
      }

      try {
        // Check if game is already cached locally
        const cachedGame = getCachedGameData(id);
        
        if (cachedGame) {
          // Use cached data (offline-first)
          setGameData(cachedGame);
          setLoading(false);
          return;
        }

        // Download game from Firestore (fan-out: only one read)
        const gameDoc = await getDoc(doc(db, 'games', id));
        if (!gameDoc.exists()) {
          setError('Game not found');
          setLoading(false);
          return;
        }

        const data = gameDoc.data();
        
        // Cache game data locally for offline use
        cacheGameData(id, data);
        
        setGameData(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load game');
        setLoading(false);
      }
    };

    loadGameLocally();
  }, [id]);

  // 🎯 FAN-OUT: Real-time sync with host via localStorage (no database charges)
  useEffect(() => {
    if (!gameData) return;
    
    // Poll localStorage for updates from host
    const interval = setInterval(() => {
      try {
        const hostStage = localStorage.getItem(`hostStage_${id}`);
        if (hostStage) {
          const parsed = JSON.parse(hostStage);
          console.log('🎯 Audience received from localStorage:', {
            stage: parsed.stage,
            revealAnswer: parsed.revealAnswer,
            scoreAnnouncementIndex: parsed.scoreAnnouncementIndex,
            teamsCount: parsed.teams?.length || 0,
            teams: parsed.teams?.map(t => ({ name: t.name, score: t.score })) || []
          });
          setCurrentStage(parsed.stage || 1);
          setRevealAnswer(parsed.revealAnswer || false);
          setScoreAnnouncementIndex(parsed.scoreAnnouncementIndex !== undefined ? parsed.scoreAnnouncementIndex : -1);
          setTeams(parsed.teams || []);
          setCurrentSlideIndex(parsed.currentSlideIndex !== undefined ? parsed.currentSlideIndex : 0);
          
          const newScoreIndex = parsed.scoreAnnouncementIndex !== undefined ? parsed.scoreAnnouncementIndex : -1;
          
          console.log('🎯 Audience state updated:', {
            newStage: parsed.stage || 1,
            newRevealAnswer: parsed.revealAnswer || false,
            newScoreAnnouncementIndex: newScoreIndex,
            newTeamsCount: parsed.teams?.length || 0
          });
          
          // Update ref immediately for render function access
          scoreAnnouncementIndexRef.current = newScoreIndex;
        }
      } catch (error) {
        console.error('Error reading host stage from localStorage:', error);
      }
    }, 100); // Check every 100ms for updates
    
    return () => clearInterval(interval);
  }, [gameData, id]);

  // Debug: Log when scoreAnnouncementIndex changes
  useEffect(() => {
    console.log('🎯 Audience scoreAnnouncementIndex changed:', scoreAnnouncementIndex);
    scoreAnnouncementIndexRef.current = scoreAnnouncementIndex;
  }, [scoreAnnouncementIndex]);

  // Fetch slides for the current venue/game (same as host)
  useEffect(() => {
    const fetchSlides = async () => {
      if (!gameData) return;
      try {
        const venueId = gameData.venueId;
        const venueCity = gameData.venueCity;
        const venueState = gameData.venueState;
        const venueCountry = gameData.venueCountry;
        const venueChain = gameData.venueChain;
        const slidesQuery = query(
          collection(db, 'slides'),
          where('active', '==', true)
        );
        const snapshot = await getDocs(slidesQuery);
        const allSlides = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        const applicableSlides = allSlides.filter(slide => {
          if (slide.targetType === 'global') return true;
          if (slide.targetType === 'venue' && slide.targetValue === venueId) return true;
          if (slide.targetType === 'city' && slide.targetValue === venueCity) return true;
          if (slide.targetType === 'state' && slide.targetValue === venueState) return true;
          if (slide.targetType === 'country' && slide.targetValue === venueCountry) return true;
          if (slide.targetType === 'chain' && slide.targetValue === venueChain) return true;
          return false;
        });
        // Sort by stage, then by order within each stage
        applicableSlides.sort((a, b) => {
          // First sort by stage priority: pregame -> halftime -> tiebreaker -> final
          const stageOrder = { pregame: 1, halftime: 2, tiebreaker: 3, final: 4 };
          const stageA = stageOrder[a.stage] || 5;
          const stageB = stageOrder[b.stage] || 5;
          
          if (stageA !== stageB) {
            return stageA - stageB;
          }
          
          // Then sort by order within each stage
          const orderA = a.order || 1;
          const orderB = b.order || 1;
          return orderA - orderB;
        });
        setSlides(applicableSlides);
      } catch (error) {
        console.error('Error fetching slides:', error);
      }
    };
    fetchSlides();
  }, [gameData]);

  const renderContent = () => {
    if (!gameData) return null;



    const getQA = (quarter, index) => quarter && quarter[index];

    const renderCategoryOverview = (quarter, label) => {
      if (!quarter) return <p className="text-red-500">No {label} data.</p>;
      
      // Check if this is a single category (halftime, final, tiebreaker)
      const isSingleCategory = quarter.length === 1;
      
      if (isSingleCategory) {
        return (
          <div className="h-[calc(100vh-5rem)] flex flex-col justify-center">
            <div className="text-center max-w-4xl mx-auto px-8">
              <div className="mb-12">
                <h2 className="text-7xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {label}
                </h2>
                <div className="w-48 h-2 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
              </div>
              
              <div className="group">
                <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-12 rounded-3xl shadow-2xl border border-gray-600 transform transition-all duration-300 group-hover:scale-105">
                  <div className="text-center">
                    <h3 className="text-6xl font-bold text-white leading-tight">{quarter[0]?.category || 'N/A'}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }
      
      // Original layout for multiple categories (quarters)
      return (
        <div className="h-[calc(100vh-5rem)] flex flex-col justify-center">
          <div className="grid grid-cols-2 gap-16 max-w-7xl mx-auto px-8">
            {/* Left Column */}
            <div className="space-y-8">
              {/* Title in left column */}
              <div className="text-center">
                <h2 className="text-7xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {label}
                </h2>
                <div className="w-48 h-2 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
              </div>
              
              {/* Box 1 */}
              <div className="group">
                <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-600 transform transition-all duration-300 group-hover:scale-105">
                  <div className="text-center">
                    <div className="text-9xl font-bold text-blue-400 mb-6 drop-shadow-lg">1</div>
                    <h3 className="text-5xl font-bold text-white leading-tight">{quarter[0]?.category || 'N/A'}</h3>
                  </div>
                </div>
              </div>
              
              {/* Box 2 */}
              <div className="group">
                <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-600 transform transition-all duration-300 group-hover:scale-105">
                  <div className="text-center">
                    <div className="text-9xl font-bold text-blue-400 mb-6 drop-shadow-lg">2</div>
                    <h3 className="text-5xl font-bold text-white leading-tight">{quarter[1]?.category || 'N/A'}</h3>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-8">
              {/* Box 3 */}
              <div className="group">
                <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-600 transform transition-all duration-300 group-hover:scale-105">
                  <div className="text-center">
                    <div className="text-9xl font-bold text-blue-400 mb-6 drop-shadow-lg">3</div>
                    <h3 className="text-5xl font-bold text-white leading-tight">{quarter[2]?.category || 'N/A'}</h3>
                  </div>
                </div>
              </div>
              
              {/* Box 4 */}
              <div className="group">
                <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-600 transform transition-all duration-300 group-hover:scale-105">
                  <div className="text-center">
                    <div className="text-9xl font-bold text-blue-400 mb-6 drop-shadow-lg">4</div>
                    <h3 className="text-5xl font-bold text-white leading-tight">{quarter[3]?.category || 'N/A'}</h3>
                  </div>
                </div>
              </div>
              
              {/* Box 5 */}
              <div className="group">
                <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-8 rounded-3xl shadow-2xl border border-gray-600 transform transition-all duration-300 group-hover:scale-105">
                  <div className="text-center">
                    <div className="text-9xl font-bold text-blue-400 mb-6 drop-shadow-lg">5</div>
                    <h3 className="text-5xl font-bold text-white leading-tight">{quarter[4]?.category || 'N/A'}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };



    const renderQuestion = (qa) => {
      if (!qa) return <p className="text-red-500">Missing question data.</p>;
      return (
        <div className="h-[calc(100vh-5rem)] flex items-center justify-center w-full">
          <div className="text-center w-full px-8">
            <div className="mb-6">
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl shadow-2xl border border-gray-700">
                {!revealAnswer && (
                  <div className="mb-6">
                    {qa.type === 'matching' ? (
                      <div className="space-y-6">
                        {/* Matching Question Instruction */}
                        <div className="mb-6">
                          <p className="text-5xl font-bold text-white leading-relaxed">
                            {qa.questionInstruction || 'Match the items below'}
                          </p>
                        </div>
                        
                        {/* Matching Columns */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Left Column */}
                          <div className="bg-gray-700 rounded-2xl p-6 border border-gray-600">
                            <div className="space-y-4 text-center">
                              {qa.leftColumn?.split('\n').map((item, index) => (
                                <div key={index} className="text-5xl text-white font-semibold">
                                  {item.trim()}
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Right Column */}
                          <div className="bg-gray-700 rounded-2xl p-6 border border-gray-600">
                            <div className="space-y-4 text-center">
                              {qa.rightColumn?.split('\n').map((item, index) => (
                                <div key={index} className="text-5xl text-white font-semibold">
                                  {item.trim()}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : qa.type === 'ordering' ? (
                      <div className="space-y-6">
                        {/* Ordering Question Instruction */}
                        <div className="mb-6">
                          <p className="text-5xl font-bold text-white leading-relaxed">
                            {qa.questionInstruction || 'Put the following items in order'}
                          </p>
                        </div>
                        {/* Ordering Items */}
                        <div className="bg-gray-700 rounded-2xl p-6 border border-gray-600">
                          <div className="space-y-4 text-center">
                            {qa.orderingItems?.split('\n').map((item, index) => (
                              <div key={index} className="text-5xl text-white font-semibold">
                                {item.trim()}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-7xl font-semibold text-white leading-relaxed">{qa.question}</p>
                    )}
                  </div>
                )}
                {!revealAnswer && qa.questionImage && (
                  <div className="mb-6">
                    <img 
                      src={qa.questionImage} 
                      alt="Question Visual" 
                      className="h-[90vh] max-w-full object-contain mx-auto rounded-2xl shadow-xl" 
                    />
                  </div>
                )}
                
                {revealAnswer ? (
                  <div className="mt-8 p-6">
                    {qa.type === 'matching' ? (
                      <div className="space-y-6">
                        {qa.answer ? (
                          qa.answer.split('\n').map((pair, index) => (
                            <div key={index} className="text-6xl font-semibold text-white">
                              {pair.trim()}
                            </div>
                          ))
                        ) : (
                          // Fallback: try to format from answerPairs if available
                          qa.answerPairs ? (
                            qa.answerPairs
                              .filter(pair => pair.left && pair.right)
                              .map((pair, index) => (
                                <div key={index} className="text-6xl font-semibold text-white">
                                  {pair.left} – {pair.right}
                                </div>
                              ))
                          ) : (
                            <p className="text-6xl font-semibold text-white mb-4">{qa.answer}</p>
                          )
                        )}
                      </div>
                    ) : qa.type === 'ordering' ? (
                      <div className="space-y-6">
                        {qa.answer ? (
                          // Check if the answer looks like the old concatenated format
                          qa.answer.includes('Sq Miles') ? (
                            // Old format detected, try to parse it better
                            (() => {
                              const items = [];
                              // Split by "Sq Miles" and then clean up each part
                              const parts = qa.answer.split('Sq Miles');
                              parts.forEach((part, index) => {
                                if (part.trim()) {
                                  // Clean up the part and add "Sq Miles" back
                                  const cleanPart = part.trim();
                                  if (cleanPart) {
                                    items.push(
                                      <div key={index} className="text-6xl font-semibold text-white">
                                        {index + 1}. {cleanPart} Sq Miles
                                      </div>
                                    );
                                  }
                                }
                              });
                              return items;
                            })()
                          ) : (
                            // New format, split by newlines
                            qa.answer.split('\n').map((item, index) => (
                              <div key={index} className="text-6xl font-semibold text-white">
                                {index + 1}. {item.trim()}
                              </div>
                            ))
                          )
                        ) : (
                          // Fallback: try to format from orderingAnswer if available
                          qa.orderingAnswer ? (
                            qa.orderingAnswer
                              .filter(item => item.trim())
                              .map((item, index) => (
                                <div key={index} className="text-6xl font-semibold text-white">
                                  {index + 1}. {item.trim()}
                                </div>
                              ))
                          ) : (
                            <p className="text-6xl font-semibold text-white mb-4">{qa.answer}</p>
                          )
                        )}
                      </div>
                    ) : (
                      <p className="text-6xl font-semibold text-white mb-4">{qa.answer}</p>
                    )}
                    {qa.answerImage && (
                      <div className="mt-4">
                        <img 
                          src={qa.answerImage} 
                          alt="Answer Visual" 
                          className="h-[60vh] max-w-full object-contain mx-auto rounded-2xl shadow-lg" 
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mt-8 p-6">
                    <p className="text-6xl font-semibold text-white">Answer will be revealed...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };

    const renderSlide = (slide) => {
      if (!slide) return <p className="text-gray-400">No slide content available.</p>;
      
      return (
        <div className="h-[calc(100vh-5rem)] flex items-center justify-center w-full">
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center w-full">
              {slide.imageUrl ? (
                <img 
                  src={slide.imageUrl} 
                  alt="Slide visual" 
                  className="max-h-[calc(100vh-6rem)] max-w-full object-contain mx-auto" 
                />
              ) : (
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-3xl shadow-2xl border border-gray-700 max-w-2xl mx-auto">
                  <div className="text-6xl mb-4">📺</div>
                  <p className="text-2xl text-gray-400 font-medium">No slide image available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    const renderScoreAnnouncement = () => {
      // Get teams from localStorage sync
      let currentTeam = null;
      let actualRank = 0;
      
      // Use ref value for immediate access to latest scoreAnnouncementIndex
      const currentIndex = scoreAnnouncementIndexRef.current;
      
      // Check for ties in final score summary
      let tieInfo = null;
      if (currentStage === 39 && teams.length > 0) {
        const sortedTeams = [...teams].sort((a, b) => Number(b.score) - Number(a.score)); // Highest to lowest
        const firstPlaceScore = sortedTeams[0]?.score || 0;
        const secondPlaceScore = sortedTeams[1]?.score || 0;
        const thirdPlaceScore = sortedTeams[2]?.score || 0;
        
        // Check for ties in top 3 positions
        if (sortedTeams.length >= 2 && firstPlaceScore === secondPlaceScore) {
          // Tie for 1st place
          tieInfo = {
            type: '1st',
            teams: sortedTeams.filter(team => team.score === firstPlaceScore),
            score: firstPlaceScore
          };
        } else if (sortedTeams.length >= 3 && secondPlaceScore === thirdPlaceScore) {
          // Tie for 2nd place
          tieInfo = {
            type: '2nd',
            teams: sortedTeams.filter(team => team.score === secondPlaceScore),
            score: secondPlaceScore
          };
        } else if (sortedTeams.length >= 4 && thirdPlaceScore === sortedTeams[3]?.score) {
          // Tie for 3rd place
          tieInfo = {
            type: '3rd',
            teams: sortedTeams.filter(team => team.score === thirdPlaceScore),
            score: thirdPlaceScore
          };
        }
      }
      
      // Determine the stage name and styling based on current stage
      let stageName, bgGradient, borderColor, emoji;
      if (currentStage === 18) {
        stageName = "Halftime Score Update";
        bgGradient = "from-blue-900 to-blue-800";
        borderColor = "border-blue-600";
        emoji = "🏈";
      } else if (currentStage === 32) {
        stageName = "Pre-Final Score Update";
        bgGradient = "from-purple-900 to-purple-800";
        borderColor = "border-purple-600";
        emoji = "📊";
      } else if (currentStage === 39) {
        stageName = tieInfo ? "Final Score Summary - TIE DETECTED!" : "Final Score Summary";
        bgGradient = tieInfo ? "from-yellow-900 to-orange-800" : "from-green-900 to-green-800";
        borderColor = tieInfo ? "border-yellow-600" : "border-green-600";
        emoji = tieInfo ? "⚡" : "🏆";
      } else {
        stageName = "Score Update";
        bgGradient = "from-blue-900 to-blue-800";
        borderColor = "border-blue-600";
        emoji = "📊";
      }
      
      console.log('🎯 Score Announcement Debug:', {
        teamsCount: teams.length,
        scoreAnnouncementIndex,
        scoreAnnouncementIndexRef: currentIndex,
        scoreAnnouncementIndexType: typeof currentIndex,
        isValidIndex: currentIndex >= 0 && currentIndex < teams.length,
        teams: teams.map(t => ({ name: t.name, score: t.score, scoreType: typeof t.score })),
        currentStage: currentStage,
        stageName: stageName
      });
      
      if (teams.length > 0 && currentIndex >= 0 && currentIndex < teams.length) {
        // Sort teams by score (lowest to highest) - start from last place, work up to first
        const sortedTeams = [...teams].sort((a, b) => Number(a.score) - Number(b.score));
        currentTeam = sortedTeams[currentIndex];
        
        // Calculate actual rank (last place = lowest score, first place = highest score)
        actualRank = teams.length - currentIndex;
        
        console.log('✅ Found current team:', {
          currentTeam,
          sortedTeams: sortedTeams.map(t => ({ name: t.name, score: t.score })),
          currentIndex,
          actualRank,
          totalTeams: teams.length
        });
      }
      
      return (
        <div className="text-center">
                      <div className={`bg-gradient-to-br ${bgGradient} p-12 rounded-3xl shadow-2xl border ${borderColor}`}>
              <h3 className="text-4xl font-bold text-white mb-8">{emoji} {stageName}</h3>
              
              {/* Tie Information Display */}
              {tieInfo && (
                <div className="mb-8 p-6 bg-gradient-to-r from-yellow-900 to-orange-900 rounded-2xl border-2 border-yellow-500">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-yellow-300 mb-4">
                      🏆 TIE FOR {tieInfo.type.toUpperCase()} PLACE! 🏆
                    </div>
                    <div className="text-xl text-yellow-200 mb-4">
                      {tieInfo.teams.length} teams tied with {tieInfo.score} points
                    </div>
                    <div className="text-lg text-yellow-100">
                      {tieInfo.teams.map(team => team.name).join(' • ')}
                    </div>
                    <div className="mt-4 text-yellow-300 font-semibold">
                      ⚡ Tiebreaker round coming next! ⚡
                    </div>
                  </div>
                </div>
              )}
              
              {/* Progress indicator */}
              <div className={`mb-8 text-lg ${currentStage === 18 ? 'text-blue-200' : currentStage === 32 ? 'text-purple-200' : tieInfo ? 'text-yellow-200' : 'text-green-200'}`}>
                {teams.length > 0 && currentIndex >= 0 && currentIndex < teams.length ? (
                  `Rank #${teams.length - currentIndex} of ${teams.length} • ${currentIndex + 1} teams announced`
                ) : (
                  'Waiting for host to start score announcement...'
                )}
              </div>
            
            {/* Current Team Display */}
            <div className={`bg-gradient-to-br ${currentStage === 18 ? 'from-blue-700 to-blue-800 border-blue-500' : currentStage === 32 ? 'from-purple-700 to-purple-800 border-purple-500' : tieInfo ? 'from-yellow-700 to-orange-800 border-yellow-500' : 'from-green-700 to-green-800 border-green-500'} rounded-2xl p-8 shadow-lg border`}>
              <div className="text-center">
                {currentTeam ? (
                  <>
                    <div className={`text-8xl font-bold mb-6 ${currentStage === 18 ? 'text-blue-300' : currentStage === 32 ? 'text-purple-300' : tieInfo ? 'text-yellow-300' : 'text-green-300'}`}>
                      #{actualRank}
                    </div>
                    <div className="text-5xl font-bold text-white mb-4">
                      {currentTeam.name}
                    </div>
                    <div className={`text-3xl ${currentStage === 18 ? 'text-blue-200' : currentStage === 32 ? 'text-purple-200' : tieInfo ? 'text-yellow-200' : 'text-green-200'}`}>
                      Current Score: <span className="font-bold text-yellow-400">{currentTeam.score} pts</span>
                    </div>
                    {currentStage === 39 && currentTeam.gpa !== undefined && (
                      <div className={`text-2xl mt-2 ${currentStage === 18 ? 'text-blue-200' : currentStage === 32 ? 'text-purple-200' : tieInfo ? 'text-yellow-200' : 'text-green-200'}`}>
                        GPA: <span className="font-bold text-blue-300">{currentTeam.gpa.toFixed(2)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className={`text-8xl font-bold mb-6 ${currentStage === 18 ? 'text-blue-300' : currentStage === 32 ? 'text-purple-300' : tieInfo ? 'text-yellow-300' : 'text-green-300'}`}>
                      ---
                    </div>
                    <div className="text-5xl font-bold text-white mb-4">
                      Waiting...
                    </div>
                    <div className={`text-3xl ${currentStage === 18 ? 'text-blue-200' : currentStage === 32 ? 'text-purple-200' : tieInfo ? 'text-yellow-200' : 'text-green-200'}`}>
                      Current Score: <span className="font-bold text-yellow-400">--- pts</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className={`mt-8 text-lg ${currentStage === 18 ? 'text-blue-200' : currentStage === 32 ? 'text-purple-200' : tieInfo ? 'text-yellow-200' : 'text-green-200'}`}>
              {tieInfo ? '⚡ Tiebreaker round will begin automatically! ⚡' : 'Host is reading scores from last place to first place'}
            </div>
          </div>
        </div>
      );
    };

    switch (currentStage) {
      case 1:
        const pregameSlides = getSlidesForCurrentStage();
        return pregameSlides.length > 0 && currentSlideIndex < pregameSlides.length 
          ? renderSlide(pregameSlides[currentSlideIndex])
          : <p className="text-gray-400">No pre-game slides available.</p>;
      case 2:
        return renderCategoryOverview(gameData.quarter1, "Quarter 1 Categories");
      case 3:
        return renderQuestion(getQA(gameData.quarter1, 0));
      case 4:
        return renderQuestion(getQA(gameData.quarter1, 1));
      case 5:
        return renderQuestion(getQA(gameData.quarter1, 2));
      case 6:
        return renderQuestion(getQA(gameData.quarter1, 3));
      case 7:
        return renderQuestion(getQA(gameData.quarter1, 4));
      case 8:
        return renderCategoryOverview(gameData.quarter2, "Quarter 2 Categories");
      case 9:
        return renderQuestion(getQA(gameData.quarter2, 0));
      case 10:
        return renderQuestion(getQA(gameData.quarter2, 1));
      case 11:
        return renderQuestion(getQA(gameData.quarter2, 2));
      case 12:
        return renderQuestion(getQA(gameData.quarter2, 3));
      case 13:
        return renderQuestion(getQA(gameData.quarter2, 4));
      case 14:
        const halftimeSlides = getSlidesForCurrentStage();
        return halftimeSlides.length > 0 && currentSlideIndex < halftimeSlides.length 
          ? renderSlide(halftimeSlides[currentSlideIndex])
          : <p className="text-gray-400">No halftime rules available.</p>;
      case 15:
        return renderCategoryOverview([gameData.halftime], "Halftime Category");
      case 16:
        return renderQuestion(gameData.halftime);
      case 17:
        const halftimeAdsSlides = getSlidesForCurrentStage();
        return halftimeAdsSlides.length > 0 && currentSlideIndex < halftimeAdsSlides.length 
          ? renderSlide(halftimeAdsSlides[currentSlideIndex])
          : <p className="text-gray-400">No halftime ads available.</p>;
      case 18:
        // Always show score announcement for halftime (no manual scoring page)
        return renderScoreAnnouncement();
      case 19:
        return renderCategoryOverview(gameData.quarter3, "Quarter 3 Categories");
      case 20:
        return renderQuestion(getQA(gameData.quarter3, 0));
      case 21:
        return renderQuestion(getQA(gameData.quarter3, 1));
      case 22:
        return renderQuestion(getQA(gameData.quarter3, 2));
      case 23:
        return renderQuestion(getQA(gameData.quarter3, 3));
      case 24:
        return renderQuestion(getQA(gameData.quarter3, 4));
      case 25:
        return renderCategoryOverview(gameData.quarter4, "Quarter 4 Categories");
      case 26:
        return renderQuestion(getQA(gameData.quarter4, 0));
      case 27:
        return renderQuestion(getQA(gameData.quarter4, 1));
      case 28:
        return renderQuestion(getQA(gameData.quarter4, 2));
      case 29:
        return renderQuestion(getQA(gameData.quarter4, 3));
      case 30:
        return renderQuestion(getQA(gameData.quarter4, 4));
      case 31:
        const preFinalAdsSlides = getSlidesForCurrentStage();
        return preFinalAdsSlides.length > 0 && currentSlideIndex < preFinalAdsSlides.length 
          ? renderSlide(preFinalAdsSlides[currentSlideIndex])
          : <p className="text-gray-400">No pre-final ads available.</p>;
      case 32:
        // Always show score announcement for pre-final (no manual scoring page)
        return renderScoreAnnouncement();
      case 33:
        const finalSlides = getSlidesForCurrentStage();
        return finalSlides.length > 0 && currentSlideIndex < finalSlides.length 
          ? renderSlide(finalSlides[currentSlideIndex])
          : <p className="text-gray-400">No pre-final slides available.</p>;
      case 34:
        return renderCategoryOverview([gameData.final], "Final Category");
      case 35:
        return renderQuestion(gameData.final);
      case 36: {
        // Check for ties immediately when on this stage
        const sortedTeams = [...teams].sort((a, b) => Number(b.score) - Number(a.score));
        const firstPlaceScore = sortedTeams[0]?.score || 0;
        const secondPlaceScore = sortedTeams[1]?.score || 0;
        const thirdPlaceScore = sortedTeams[2]?.score || 0;
        
        let hasTie = false;
        let tieInfo = null;
        
        // Check for ties in top 3 positions
        if (sortedTeams.length >= 2 && firstPlaceScore === secondPlaceScore) {
          hasTie = true;
          tieInfo = {
            type: '1st',
            teams: sortedTeams.filter(team => team.score === firstPlaceScore),
            score: firstPlaceScore
          };
        } else if (sortedTeams.length >= 3 && secondPlaceScore === thirdPlaceScore) {
          hasTie = true;
          tieInfo = {
            type: '2nd',
            teams: sortedTeams.filter(team => team.score === secondPlaceScore),
            score: secondPlaceScore
          };
        } else if (sortedTeams.length >= 4 && thirdPlaceScore === sortedTeams[3]?.score) {
          hasTie = true;
          tieInfo = {
            type: '3rd',
            teams: sortedTeams.filter(team => team.score === thirdPlaceScore),
            score: thirdPlaceScore
          };
        }
        
        return (
          <div className="text-center">
            <div className="bg-gradient-to-r from-blue-900 to-purple-900 p-8 rounded-2xl border border-blue-600 mb-6">
              <div className="flex items-center justify-center space-x-4">
                <span className="text-blue-400 text-4xl">🔍</span>
                <h2 className="text-3xl font-bold text-blue-200">
                  CHECKING FOR TIES
                </h2>
                <span className="text-blue-400 text-4xl">🔍</span>
              </div>
              
              {hasTie ? (
                <div className="mt-6">
                  <div className="bg-gradient-to-r from-yellow-900 to-orange-900 p-6 rounded-xl border-2 border-yellow-500 mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-300 mb-4">
                        🏆 TIE FOR {tieInfo.type.toUpperCase()} PLACE! 🏆
                      </div>
                      <div className="text-xl text-yellow-200 mb-4">
                        {tieInfo.teams.length} teams tied with {tieInfo.score} points
                      </div>
                      <div className="text-lg text-yellow-100 mb-4">
                        {tieInfo.teams.map(team => team.name).join(' • ')}
                      </div>
                      <div className="text-yellow-300 font-semibold">
                        ⚡ Tiebreaker round will begin! ⚡
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-6">
                  <div className="bg-gradient-to-r from-green-900 to-emerald-900 p-6 rounded-xl border-2 border-green-500 mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-300 mb-4">
                        ✅ NO TIES DETECTED
                      </div>
                      <div className="text-xl text-green-200">
                        All teams have different scores. Proceeding to final results.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }
      case 37:
        const tiebreakerSlides = getSlidesForCurrentStage();
        return tiebreakerSlides.length > 0 && currentSlideIndex < tiebreakerSlides.length 
          ? renderSlide(tiebreakerSlides[currentSlideIndex])
          : <p className="text-gray-400">No tiebreaker info available.</p>;
      case 38:
        return renderQuestion(gameData.tieBreaker);
      case 39:
        // Always show score announcement for final (no manual scoring page)
        return renderScoreAnnouncement();
      case 40:
        return (
          <div className="text-center">
            <div className="mb-12">
              <h2 className="text-8xl font-bold mb-8 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                🎉 Game Over
              </h2>
              <div className="w-48 h-2 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full"></div>
            </div>
            
            {/* Final Results - All Teams */}
            <div className="bg-gradient-to-br from-purple-900 to-pink-900 p-12 rounded-3xl shadow-2xl border border-purple-700 mb-8">
              <h3 className="text-5xl font-bold text-white mb-8">🏆 Final Results</h3>
              
              {teams.length > 0 ? (
                <div className="space-y-6">
                  {/* Sort teams by score (highest to lowest) */}
                  {[...teams].sort((a, b) => Number(b.score) - Number(a.score)).map((team, index) => (
                    <div key={team.name} className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-2xl border border-gray-600">
                      <div className="flex items-center justify-between">
                        {/* Rank and Team Name */}
                        <div className="flex items-center space-x-6">
                          <div className="text-6xl font-bold text-purple-400">
                            #{index + 1}
                          </div>
                          <div className="text-left">
                            <div className="text-4xl font-bold text-white mb-2">
                              {team.name}
                            </div>
                            <div className="text-2xl text-gray-300">
                              Score: <span className="font-bold text-yellow-400">{team.score} pts</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* GPA */}
                        <div className="text-right">
                          <div className="text-3xl font-bold text-blue-300">
                            GPA: {team.gpa !== undefined ? team.gpa.toFixed(2) : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-3xl text-gray-400">
                  No teams participated in this game.
                </div>
              )}
            </div>
            
            {/* Thank You Message */}
            <div className="bg-gradient-to-br from-purple-900 to-pink-900 p-8 rounded-3xl shadow-2xl border border-purple-700">
              <div className="text-6xl mb-4">🎉</div>
              <p className="text-3xl text-gray-300 font-medium">Thank you for playing!</p>
            </div>
          </div>
        );
      default:
        return <p className="text-gray-400">Loading...</p>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading audience display...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-red-400 text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Logo in bottom right */}
      {gameData && (
        <div className="fixed bottom-4 right-4 z-20">
          <img 
            src="/triviaplug-logo.png" 
            alt="TriviaPlug Logo" 
            className="h-28 w-auto drop-shadow-lg brightness-0 invert"
          />
        </div>
      )}
      
      {/* Progress Indicator for Audience - Fixed Header */}
      {gameData && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-gray-700 p-4">
          <div className="w-full px-6">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-white">
                    {getProgressInfo(currentStage).quarter}
                  </div>
                </div>
                {getProgressInfo(currentStage).question && typeof getProgressInfo(currentStage).question === 'number' && 
                 getProgressInfo(currentStage).totalQuestions > 1 && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">
                      Question {getProgressInfo(currentStage).question}
                    </div>
                    <div className="text-sm text-gray-400">of {getProgressInfo(currentStage).totalQuestions}</div>
                  </div>
                )}
              </div>
              
              {/* Upcoming Categories */}
              <div className="flex-1 ml-8">
                <div className="flex items-center gap-2 w-full">
                  <div className="text-4xl font-semibold text-gray-300 whitespace-nowrap">Upcoming Categories:</div>
                  <div className="flex flex-wrap gap-8 flex-1">
                    {getUpcomingCategories(currentStage, gameData).map((category, index) => (
                      <span 
                        key={index}
                        className={`text-4xl font-bold ${
                          index === 0 
                            ? 'text-yellow-400' 
                            : 'text-gray-300'
                        }`}
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content - Full Screen for Slides */}
      <div className={`${gameData ? 'pt-20' : 'pt-8'}`}>
        {renderContent()}
      </div>
    </div>
  );
};

export default AudienceDisplay;
